/**
 * 品牌管理列表页
 *
 * 状态筛选 + DataGrid 表格 + 创建/编辑/配置/二维码操作。
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  AlertColor,
} from '@mui/material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit'
import TuneIcon from '@mui/icons-material/Tune'
import QrCodeIcon from '@mui/icons-material/QrCode'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { AdminLayout } from '@/components/AdminLayout'
import { BrandFormDialog } from '@/components/BrandFormDialog'
import { BrandConfigDialog } from '@/components/BrandConfigDialog'
import {
  getBrands,
  createBrand,
  updateBrand,
  deleteBrand,
  updateBrandConfig,
  getBrandQrcode,
  getBrandDetail,
} from '@/api/services'
import type { Brand, BrandConfig } from '@chaoyinli/shared'
import type { BrandCreateParams, BrandUpdateParams, BrandConfigParams, QrcodeResponse } from '@/api/services'
import { formatDate, formatDateOnly } from '@/utils/format'

/** 品牌状态映射 */
const STATUS_LABELS: Record<string, string> = {
  trial: '试用中',
  active: '已激活',
  expired: '已过期',
}

const STATUS_COLORS: Record<string, string> = {
  trial: '#FF9800',
  active: '#4CAF50',
  expired: '#F44336',
}

/** 品类分隔符：英文逗号、中文逗号、顿号、斜杠、竖线、分号 */
const CATEGORY_SPLIT_REGEX = /[,，、/|;；\s]+/

/**
 * 将多值品类字符串归一化为「、」拼接展示。
 * @param raw 原始品类字符串，如 "小龙虾/夜宵"
 * @returns 展示文本，如 "小龙虾、夜宵"；空值返回 '-'
 */
function formatCategoryText(raw: string | null | undefined): string {
  if (!raw) return '-'
  const items = String(raw)
    .trim()
    .split(CATEGORY_SPLIT_REGEX)
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
  return items.length > 0 ? items.join('、') : '-'
}

/** 状态筛选 Tab */
const STATUS_TABS = [
  { label: '全部', value: '' },
  { label: '试用中', value: 'trial' },
  { label: '已激活', value: 'active' },
  { label: '已过期', value: 'expired' },
]

export function BrandsPage() {
  const navigate = useNavigate()

  // 列表数据
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')

  // 弹窗状态
  const [formOpen, setFormOpen] = useState(false)
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null)
  const [configOpen, setConfigOpen] = useState(false)
  const [configBrand, setConfigBrand] = useState<{ brand: Brand; config: BrandConfig | null } | null>(null)
  const [qrcodeData, setQrcodeData] = useState<QrcodeResponse | null>(null)
  const [qrcodeOpen, setQrcodeOpen] = useState(false)
  // 删除二次确认
  const [deletingBrand, setDeletingBrand] = useState<Brand | null>(null)

  // 提交状态
  const [submitting, setSubmitting] = useState(false)

  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const showToast = (message: string, severity: AlertColor = 'info') => {
    setToast({ open: true, message, severity })
  }

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }))
  }

  // 加载品牌列表
  const fetchBrands = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getBrands(statusFilter || undefined)
      setBrands(data)
    } catch (e) {
      const msg = e instanceof Error ? e.message : '加载品牌列表失败'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    fetchBrands()
  }, [fetchBrands])

  // 创建品牌
  const handleCreate = () => {
    setEditingBrand(null)
    setFormOpen(true)
  }

  // 编辑品牌
  const handleEdit = (brand: Brand) => {
    setEditingBrand(brand)
    setFormOpen(true)
  }

  // 提交品牌表单
  const handleFormSubmit = async (data: BrandCreateParams | BrandUpdateParams) => {
    setSubmitting(true)
    try {
      if (editingBrand) {
        await updateBrand(editingBrand.id, data as BrandUpdateParams)
        showToast('品牌信息已更新', 'success')
      } else {
        await createBrand(data as BrandCreateParams)
        showToast('品牌创建成功', 'success')
      }
      setFormOpen(false)
      fetchBrands()
    } catch (e) {
      const msg = e instanceof Error ? e.message : '操作失败'
      showToast(msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // 打开删除二次确认
  const handleDeleteRequest = (brand: Brand) => {
    setDeletingBrand(brand)
  }

  // 关闭删除确认弹窗
  const handleDeleteCancel = () => {
    if (submitting) return
    setDeletingBrand(null)
  }

  // 确认删除品牌
  const handleDeleteConfirm = async () => {
    if (!deletingBrand) return
    setSubmitting(true)
    try {
      await deleteBrand(deletingBrand.id)
      showToast(`品牌「${deletingBrand.name}」已删除`, 'success')
      setDeletingBrand(null)
      fetchBrands()
    } catch (e) {
      const msg = e instanceof Error ? e.message : '删除失败'
      showToast(msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // 打开配置弹窗
  const handleConfig = async (brand: Brand) => {
    try {
      const detail = await getBrandDetail(brand.id)
      setConfigBrand({ brand: detail, config: detail.config || null })
      setConfigOpen(true)
    } catch {
      // 如果获取详情失败，使用空配置
      setConfigBrand({ brand, config: null })
      setConfigOpen(true)
    }
  }

  // 提交品牌配置
  const handleConfigSubmit = async (config: BrandConfigParams) => {
    if (!configBrand) return
    setSubmitting(true)
    try {
      await updateBrandConfig(configBrand.brand.id, config)
      showToast('品牌配置已保存', 'success')
      setConfigOpen(false)
    } catch (e) {
      const msg = e instanceof Error ? e.message : '保存配置失败'
      showToast(msg, 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // 查看通用入口二维码（老板 / 员工共用同一个码，角色由登录密码决定）
  const handleQrcode = async (brand: Brand) => {
    try {
      const data = await getBrandQrcode(brand.id)
      setQrcodeData(data)
      setQrcodeOpen(true)
    } catch (e) {
      const msg = e instanceof Error ? e.message : '获取二维码失败'
      showToast(msg, 'error')
    }
  }

  // DataGrid 列定义
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: '品牌名称',
      width: 160,
      renderCell: (params) => (
        <Box
          sx={{ cursor: 'pointer', color: '#FF8C00', fontWeight: 600 }}
          onClick={() => navigate(`/brands/${params.row.id}`)}
        >
          {params.value}
        </Box>
      ),
    },
    { field: 'sub_brand', headerName: '子品牌', width: 120, valueGetter: (v) => v || '-' },
    {
      field: 'category',
      headerName: '品类',
      width: 140,
      // 品类为多值（逗号/斜杠分隔），列表统一以「、」展示
      valueGetter: (v) => formatCategoryText(v as string),
    },
    {
      // 列表脱敏展示：后端返回前4后2中间*的口令
      field: 'access_code',
      headerName: '品牌口令',
      width: 130,
      valueGetter: (v) => v || '-',
    },
    {
      field: 'status',
      headerName: '状态',
      width: 100,
      renderCell: (params) => {
        const color = STATUS_COLORS[params.value] || '#999'
        return (
          <Box
            sx={{
              display: 'inline-block',
              px: 1,
              py: 0.3,
              borderRadius: '4px',
              backgroundColor: `${color}15`,
              color: color,
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {STATUS_LABELS[params.value] || params.value}
          </Box>
        )
      },
    },
    {
      field: 'trial_end',
      headerName: '试用截止',
      width: 120,
      valueGetter: (v) => formatDateOnly(v),
    },
    {
      field: 'quota_pool',
      headerName: '额度池剩余',
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as { quota_total?: number; quota_used?: number }
        const total = row.quota_total ?? 0
        const used = row.quota_used ?? 0
        return (
          <Typography
            variant="body2"
            sx={{
              color: '#333',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            剩余 {Math.max(0, total - used)} 点 / 共 {total} 点
          </Typography>
        )
      },
    },
    {
      field: 'created_at',
      headerName: '创建时间',
      width: 160,
      valueGetter: (v) => formatDate(v),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 260,
      sortable: false,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          key="view"
          icon={
            <Tooltip title="查看详情">
              <VisibilityIcon fontSize="small" />
            </Tooltip>
          }
          label="查看"
          onClick={() => navigate(`/brands/${params.row.id}`)}
        />,
        <GridActionsCellItem
          key="edit"
          icon={
            <Tooltip title="编辑信息">
              <EditIcon fontSize="small" />
            </Tooltip>
          }
          label="编辑"
          onClick={() => handleEdit(params.row as Brand)}
        />,
        <GridActionsCellItem
          key="config"
          icon={
            <Tooltip title="配置调性">
              <TuneIcon fontSize="small" />
            </Tooltip>
          }
          label="配置"
          onClick={() => handleConfig(params.row as Brand)}
        />,
        <GridActionsCellItem
          key="qrcode"
          icon={
            <Tooltip title="通用入口二维码">
              <QrCodeIcon fontSize="small" sx={{ color: '#FF8C00' }} />
            </Tooltip>
          }
          label="二维码"
          onClick={() => handleQrcode(params.row as Brand)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={
            <Tooltip title="删除客户">
              <DeleteOutlineIcon fontSize="small" sx={{ color: '#F44336' }} />
            </Tooltip>
          }
          label="删除"
          onClick={() => handleDeleteRequest(params.row as Brand)}
        />,
      ],
    },
  ]

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          品牌管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            backgroundColor: '#FF8C00',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#E67E00' },
          }}
        >
          创建品牌
        </Button>
      </Box>

      {/* 状态筛选 */}
      <Tabs
        value={statusFilter}
        onChange={(_, val) => setStatusFilter(val)}
        sx={{
          mb: 2,
          '& .MuiTab-root': { textTransform: 'none', minWidth: 80 },
          '& .Mui-selected': { color: '#FF8C00' },
          '& .MuiTabs-indicator': { backgroundColor: '#FF8C00' },
        }}
      >
        {STATUS_TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>

      {/* 品牌列表 */}
      <Box sx={{ height: 'calc(100vh - 220px)', minHeight: 400, backgroundColor: '#fff', borderRadius: '12px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C00' }} />
          </Box>
        ) : (
          <DataGrid
            rows={brands}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
            }}
          />
        )}
      </Box>

      {/* 品牌创建/编辑弹窗 */}
      <BrandFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        brand={editingBrand}
        submitting={submitting}
      />

      {/* 品牌配置弹窗 */}
      <BrandConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        onSubmit={handleConfigSubmit}
        config={configBrand?.config || null}
        brandName={configBrand?.brand.name}
        submitting={submitting}
      />

      {/* 删除二次确认弹窗 */}
      <Dialog open={!!deletingBrand} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '18px' }}>删除客户</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#333', fontSize: '14px' }}>
            确定要删除品牌「{deletingBrand?.name || ''}」吗？
          </DialogContentText>
          <DialogContentText sx={{ color: '#999', fontSize: '13px', mt: 1 }}>
            删除后该客户不再出现在列表中，历史笔记与订单数据仍会保留，可联系技术恢复。
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleDeleteCancel} disabled={submitting} sx={{ color: '#666' }}>
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={submitting}
          >
            {submitting ? '删除中...' : '确认删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 二维码弹窗 */}
      <Dialog open={qrcodeOpen} onClose={() => setQrcodeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, textAlign: 'center' }}>
          {qrcodeData?.brand_name || '品牌二维码'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {qrcodeData?.qrcode_url && (
              <Box
                component="img"
                src={qrcodeData.qrcode_url}
                alt="二维码"
                sx={{
                  width: 240,
                  height: 240,
                  margin: '0 auto',
                  display: 'block',
                  border: '1px solid #f0f0f0',
                  borderRadius: '8px',
                }}
              />
            )}
            {qrcodeData?.h5_url && (
              <Typography
                variant="body2"
                sx={{ color: '#999', mt: 2, wordBreak: 'break-all', fontSize: '12px' }}
              >
                H5 链接：{qrcodeData.h5_url}
              </Typography>
            )}
            {/* 打开方式引导：微信 / App 内置 WebView 的 localStorage 不持久，
                关闭窗口即丢登录态，导致「每次扫码都要重新登录」。 */}
            <Alert
              severity="info"
              icon={false}
              sx={{
                mt: 2,
                borderRadius: '8px',
                fontSize: '12px',
                textAlign: 'left',
                lineHeight: 1.8,
              }}
            >
              请用手机<strong>系统浏览器</strong>扫描；若用微信扫，点右上角 ··· →
              在浏览器打开。登录后可把本页「添加到主屏幕」，下次免登录直接进。
            </Alert>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  )
}

export default BrandsPage
